import React, { useState, useRef, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import DOMPurify from 'dompurify';
import './index.less';
import { STATIC_RESOURCE } from "@/utils/tools";
import { agentQuestionType, agentMessage, agentMessageLogs } from './api';
const AI_AVATAR = `${STATIC_RESOURCE}/cp_gsp/home/Robot2.png`;
const USER_AVATAR = `${STATIC_RESOURCE}/cp_gsp/home/RobotUser.png`;
const AI_AVATAR_PIC = `${STATIC_RESOURCE}/cp_gsp/home/RobotAiPic.png`;
const AiAssistant = ({ closeAiChat }) => {
  const loadRef = useRef(null);
  const scrollWrapRef = useRef(null);
  const scrollTimerRef = useRef(null);
  const [currentTabId, setCurrentTabId] = useState(0);
  const [tabList, setTabList] = useState([]);
  const [chatList, setChatList] = useState([]);

  const dedupeChatList = (list) => {//去重
    const map = new Map();
    list.forEach(item => {
      const key = item.id || item._id;
      if (!map.has(key)) {
        map.set(key, item);
      }
    });
    return Array.from(map.values());
  };

  // 分页状态
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);

  const parsedListMessage = useCallback((item) => {
    const str = item?.message || '';
    const messageType = item?.message_type || 'list';
    if (!str) return [];
    try {
      const parsed = JSON.parse(str);
      if (Array.isArray(parsed)) {
        return parsed.map(i => ({
          message: i.message,
          message_type: i.message_type || messageType
        }));
      }
      console.warn('FAQ消息格式不支持:', parsed);
      return [];
    } catch (e) {
      console.warn('FAQ消息解析失败:', e);
      return [];
    }
  }, []);

  // 清洗富文本html
  const sanitizeHtml = useCallback((html) => {
    if (!html || typeof html !== 'string') return '';
    return html
      .replace(/href=['"]\s*`([^`]+)`\s*['"]/gi, 'href="$1"')
      .replace(/<a([^>]*)>/gi, '<a$1 style="color: #007aff; text-decoration: underline;">')
      .replace(/\s+/g, ' ')
      .replace(/<p\s*>/gi, '<p>')
      .replace(/<\/p\s*>/gi, '</p>');
  }, []);

  // 打开链接
  const openLink = (href) => {
    if (!href) return;
    window.open(href, '_blank');
  };

  // 滚动到底部
  const scrollToBottom = useCallback(() => {
    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    if (!scrollWrapRef.current) return;
    scrollTimerRef.current = window.setTimeout(() => {
      scrollWrapRef.current.scrollTop = scrollWrapRef.current.scrollHeight;
      scrollTimerRef.current = null;
    }, 50);
  }, []);

  const formatMessageTime = (timeStr) => {
    if (!timeStr) return '';
    return dayjs(timeStr).format('HH:mm');
  };

  const getTimestamp = () => dayjs().format('YYYY-MM-DD HH:mm:ss');

  const createMessage = useCallback((msg, index) => {
    return {
      ...msg,
      _id: Date.now() + index + 1,
      whose: 2,
      parsedMessage: msg.message_type === 'list' ? parsedListMessage(msg) : sanitizeHtml(String(msg.message)),
      create_time: getTimestamp()
    };
  }, [parsedListMessage, sanitizeHtml]);

  // 加载历史消息分页
  const loadHistoryMessages = useCallback(async () => {
    if (loadingHistory || !hasMoreHistory) return;
    setLoadingHistory(true);

    const scrollDom = scrollWrapRef.current;
    const prevScrollHeight = scrollDom ? scrollDom.scrollHeight : 0;

    try {
      const res = await agentMessageLogs({ page, page_size: PAGE_SIZE });
      const { code, data } = res.data;
      if (+code === 0) {
        const newList = data?.list || [];
        if (newList.length === 0) {
          setHasMoreHistory(false);
        } else {
          const historyMessages = newList.map(item => ({
            ...item,
            _id: item._id || `${item.id}${item.whose}${item.created_at}`,
            parsedMessage: item.message_type === 'list'
              ? parsedListMessage(item)
              : sanitizeHtml(item.message),
          }));
          setChatList(prev => dedupeChatList([...historyMessages, ...prev]));
          setPage(p => p + 1);
          setTimeout(() => {
            if (scrollWrapRef.current) {
              scrollWrapRef.current.scrollTop = scrollWrapRef.current.scrollHeight - prevScrollHeight;
            }
          }, 0);

          if (newList.length < PAGE_SIZE) {
            setHasMoreHistory(false);
          }
        }
      }
    } catch (e) {
      console.error('加载历史消息失败', e);
    } finally {
      setLoadingHistory(false);
    }
  }, [page, loadingHistory, hasMoreHistory, parsedListMessage, sanitizeHtml]);

  const sendTabMessage = async (item) => {
    try {
      setCurrentTabId(item.id);
      // 用户消息
      const userMsg = {
        ...item,
        _id: Date.now(),
        whose: 1,
        message_type: 'text',
        parsedMessage: item.message || '',
        create_time: getTimestamp()
      };
      setChatList(prev => dedupeChatList([...prev, userMsg]));

      const res = await agentMessage(item);
      const { code, data } = res.data;
      if (+code === 0 && data?.list?.length) {
        const msgs = data.list.map((msg, idx) => createMessage(msg, idx));
        setChatList(prev => dedupeChatList([...prev, ...msgs]));
      }
    } catch (e) {
      alert('发送失败，请重试');
    }
  };

  const handleFaqClick = async (faq) => {
    try {
      const faqText = faq.message || faq.title || String(faq);
      const userMsg = {
        _id: Date.now(),
        whose: 1,
        message_type: 'text',
        message: faqText,
        parsedMessage: faqText,
        create_time: getTimestamp()
      };
      setChatList(prev => dedupeChatList([...prev, userMsg]));
      const res = await agentMessage(faq);
      const { code, data } = res.data;
      if (+code === 0 && data?.list?.length) {
        const msgs = data.list.map((msg, idx) => createMessage(msg, idx));
        setChatList(prev => dedupeChatList([...prev, ...msgs]));
      }
    } catch (e) {
      alert('发送失败，请重试');
    }
  };

  // 滚动监听
  const handleScroll = useCallback((e) => {
    const dom = e.target;
    if (dom.scrollTop <= 10 && hasMoreHistory && !loadingHistory) {
      loadHistoryMessages();
    }
  }, [hasMoreHistory, loadingHistory, loadHistoryMessages]);
  useEffect(() => {
    loadRef.current = loadHistoryMessages;
  }, [loadHistoryMessages]);
  useEffect(() => {
    const initPage = async () => {
      setPage(1);
      setHasMoreHistory(true);
      setChatList([]);

      try {
        const tabRes = await agentQuestionType();
        if (+tabRes.data.code === 0) {
          setTabList(tabRes.data.data.list || []);
        }
      } catch (e) {
        alert('获取失败');
      }
      await loadRef.current();
    };
    initPage();
  }, []);

  // 新增消息自动滚到底部
  useEffect(() => {
    scrollToBottom();
  }, [chatList, scrollToBottom]);

  const handleLinkClick = (e) => {
    const target = e.target;
    const aEl = target.closest('a');
    if (!aEl) return;
    e.preventDefault();
    let jumpUrl = '';
    const hrefVal = aEl.getAttribute('href');
    const urlVal = aEl.getAttribute('url');
    const httpReg = /^https?:\/\//;
    if (hrefVal && httpReg.test(hrefVal)) {
      jumpUrl = hrefVal;
    }
    else if (urlVal && httpReg.test(urlVal)) {
      jumpUrl = urlVal;
    }
    if (jumpUrl) {
      openLink(jumpUrl);
    }
  };
  return (
    <div className="chat-ai-container">
      <div className="ai-modal-mask" onClick={closeAiChat}>
        <div className="ai-modal-wrap" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <div className="header-content">
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <img src={AI_AVATAR} className="ai-float-icon1" alt="AI助手" />
                <span className="title">小正同学智能助手</span>
              </span>
              <span className="close-btn" onClick={closeAiChat}>
                <img src={`${STATIC_RESOURCE}/cp_gsp/home/RobotClose.png`} alt="关闭" />
              </span></div>
            <div className="tab-notice-wrap">
              <div className="tab-bar">
                {tabList.map((item) => (
                  <div
                    key={item.id}
                    className={`tab-item ${currentTabId === item.id ? 'active' : ''}`}
                    onClick={() => sendTabMessage(item)}
                  >
                    <img className="tab-icon" src={item.icon_url} alt="" />
                    <span className="tab-text">{item.title}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className='ai-content'>
            <div className="notice-bar">
              <span className="notice-text">
                您可选择问题类型进行提问，当前功能正在持续搭建中，后续将逐步完善问答能力，暂未能解答的问题，敬请谅解，我们会尽快补充完善
              </span>
            </div>
            <div
              className="message-scroll-wrap"
              ref={scrollWrapRef}
              onScroll={handleScroll}
            >
              {loadingHistory && (
                <div style={{ textAlign: 'center', padding: '8px 0', color: '#666', fontSize: 13 }}>
                  加载中...
                </div>
              )}
              <div className="message-list">
                {chatList.map((item) => (
                  <div className="message-wrapper" key={item.id || item?._id}>
                    {item.whose === 1 ? (
                      // 用户消息
                      <div className="message-item self">
                        <div className="bubble-with-time">
                          <div className="message-bubble self-bubble">
                            {item.message_type === 'text' && (
                              <div
                                className="message-content"
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.parsedMessage) }}
                                onClick={(e) => { handleLinkClick(e); }}
                              />
                            )}
                          </div>
                          <div className="message-time self">{formatMessageTime(item.create_time)}</div>
                        </div>
                        <img className="avatar self-avatar" src={USER_AVATAR} alt="" />
                      </div>
                    ) : (
                      // AI消息
                      <div className="message-item ai">
                        <img className="avatar ai-avatar" src={AI_AVATAR_PIC} alt="" />
                        <div className="bubble-with-time">
                          {item.message_type === 'text' ? (
                            <div className="message-bubble ai-bubble">
                              <div
                                className="message-content"
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.parsedMessage) }}
                                onClick={(e) => { handleLinkClick(e); }}
                              />
                            </div>
                          ) : item.message_type === 'list' ? (
                            <div className="faq-container">
                              <span className="faq-title">常见问题</span>
                              {item.parsedMessage.map((faq, idx) => (
                                <div
                                  key={idx}
                                  className="faq-item-text"
                                  onClick={() => handleFaqClick(faq)}
                                >
                                  {faq.message || faq.title || faq}
                                </div>
                              ))}
                            </div>
                          ) : null}
                          <div className="message-time">{formatMessageTime(item.create_time)}</div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiAssistant;